/**
 * The route-data key a screen sets to say it does not need the server (033 FR-022).
 *
 * 021's shell hides the router outlet while offline and states the offline condition in its place,
 * because every screen there showed data the server had just computed and a cached copy would be a
 * lie (021's cut of offline reads). The guide is the first screen for which that reasoning does not
 * hold: it is static text already sitting on the device.
 *
 * A route flag rather than a URL test in the shell. `startsWith('/help')` would work today and
 * would put knowledge of one feature's paths inside the layout, where the next offline-capable
 * screen has to remember to add itself. Opting in beside the route is the declaration, at the place
 * someone writing a route is already looking.
 *
 * A constant rather than a bare string, because route `data` is an index signature: a typo in
 * either half of `data: { worksOffline: true }` / `data['worksOffline']` is silent, and the failure
 * it produces — a screen that renders blank while offline — looks like a caching bug.
 */
export const WORKS_OFFLINE = 'worksOffline';

/**
 * 036 (issue 219) — the route-data key that tells `app-root.tsx` a shell FARTHER DOWN THE TREE will
 * claim the offline panel for this route, so the root must never show its own for it — checked
 * through the SAME `useMatches()` mechanism `WORKS_OFFLINE` already uses, and for the same reason:
 * a URL test in the root would put knowledge of the shell's own paths in the one file that is
 * supposed to know nothing about routing beyond "is anything claimed".
 *
 * `core/pwa/offline-presentation.ts`'s claim count is still the right MECHANISM for "which shell is
 * currently mounted" — `layout.tsx`'s own `useEffect` claims and releases it exactly as before, and
 * that is still what a SECOND authenticated surface (there is only one today, but the guarantee
 * should not rest on that) would coordinate through. What this constant closes is the WINDOW between
 * the route being matched and that effect actually running: `layout.tsx`'s route is loaded through
 * React Router's `lazy` (034's own comment on why: the router awaits the import as part of the
 * navigation, so nothing suspends mid-render), which is itself an asynchronous step between a match
 * and a mount. `useMatches()` resolves from the URL alone, synchronously, on the SAME render `App
 * Root` uses to decide `showPanel` — before the lazy import even starts — so checking it there
 * closes the gap a `useEffect` fired from inside the lazy module cannot.
 *
 * Found by `e2e-pwa/pwa.spec.ts`'s own "opens to its own interface" and "shows no task data" going
 * red, reproducibly, on the desktop project, once an offline LAUNCH started rendering the
 * authenticated shell at all (036, issue 219) rather than redirecting to `/sign-in` first — a route
 * outside the shell every time before this cycle, so the race this closes was never reachable.
 */
export const CLAIMS_OFFLINE_PANEL = 'claimsOfflinePanel';

/**
 * 036 (issue 208, FR-021, research R-021) — the route-data key for a screen that draws a STORED
 * VIEW rather than static text. Set on all eleven of FR-021's screens: home (issue 208), and —
 * issue 221 — the contexts list, the projects list, a single context, a single project, the
 * tickler, the tags list, a single tag, review, the recurring list, and the first page of done.
 * `app.routes.readable-offline.spec.ts` is the exhaustive guard that keeps this list from growing a
 * twelfth member unnoticed. `layout.tsx` reads it the same way it reads `WORKS_OFFLINE` — a route
 * carrying either flag keeps its own outlet and never falls back to 021's offline panel — but the
 * two are not one flag wearing two names: `WORKS_OFFLINE` is for a screen that needs no server at
 * all (the guide, `/unsent`'s own local reads), where `READABLE_OFFLINE` is for one that shows the
 * server's LAST answer and says so (the stored-list line, FR-024). `/unsent` carries both, for two
 * different reasons that happen to agree on this screen: it works offline because it reads nothing
 * but the device, and it would also be readable offline in the FR-021 sense if it ever needed a
 * stored server answer, which today it does not — it is not one of FR-021's own eleven, and the
 * guard above names it separately for exactly that reason.
 */
export const READABLE_OFFLINE = 'readableOffline';
